import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { XMarkIcon, PaperClipIcon, TrashIcon, SparklesIcon, ArrowPathIcon, PlusIcon } from '@heroicons/react/24/outline';
import CreatableSelect from 'react-select/creatable';
import { contentAPI } from '../../api/contentApi';
import { salesApiService } from '../../api/salesApi';
import { subAgencyAPI, addressAPI } from '../../api/api';
import SubAgencyFormModal from '../../pages/salescall/SubAgencyFormModal';

// DateTimePicker Component
const DateTimePicker = ({ value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? value.split(' ')[0] : '');
  const [selectedTime, setSelectedTime] = useState(value ? value.split(' ')[1] || '' : '');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const generateCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handleDateSelect = (day, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (day) {
      const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setSelectedDate(formattedDate);
      const dateTimeValue = selectedTime ? `${formattedDate} ${selectedTime}` : formattedDate;
      onChange({ target: { value: dateTimeValue } });
      setIsOpen(false);
    }
  };

  const handleTimeChange = (e) => {
    const time = e.target.value;
    setSelectedTime(time);
    const dateTimeValue = selectedDate ? `${selectedDate} ${time}` : time;
    onChange({ target: { value: dateTimeValue } });
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const formatDisplayDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const [date, time] = dateTimeString.split(' ');
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return time ? `${day}/${month}/${year} ${time}` : `${day}/${month}/${year}`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-1 w-full">
        <input
          type="text"
          value={formatDisplayDateTime(value || '')}
          onChange={() => { }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          placeholder={placeholder}
          name={name}
          readOnly
          className="flex-1 min-w-0 px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="time"
          value={selectedTime}
          onChange={handleTimeChange}
          className="w-20 px-1.5 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {isOpen && (
        <div
          className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-64"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevMonth();
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              ←
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleNextMonth();
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="p-0.5 text-xs text-center text-gray-500 dark:text-gray-400 font-medium">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {generateCalendar().map((day, index) => (
              <button
                key={index}
                type="button"
                onClick={(e) => handleDateSelect(day, e)}
                disabled={!day}
                className={`
                  p-0.5 text-xs rounded transition-colors
                  ${!day ? 'invisible' : ''}
                  ${day ? 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-900 dark:text-white' : ''}
                  ${selectedDate && parseInt(selectedDate.split('-')[2]) === day ? 'bg-blue-600 text-white' : ''}
                `}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SalesCallModal = ({ isOpen, onClose, onSave, onDeleteAttachment, initialData = null, availableCalls = [] }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateLang, setTranslateLang] = useState('English');
  const [hotels, setHotels] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [subAgencies, setSubAgencies] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [isAgencyLoading, setIsAgencyLoading] = useState(false);
  const [representatives, setRepresentatives] = useState([]);
  const [isRepresentativeLoading, setIsRepresentativeLoading] = useState(false);
  const [isSubAgencyModalOpen, setIsSubAgencyModalOpen] = useState(false);
  const [agencySearchTerm, setAgencySearchTerm] = useState('');
  const hasFetchedAgencyRef = useRef(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    agency: '',
    agency_contact: '',
    phone: '',
    type: '',
    country: '',
    city: '',
    address: '',
    communication_type: '',
    call_status: '',
    call_result: '',
    summary: '',
    summary_en: '',
    sales_representative: '',
    pax: '',
    related_call: '',
    hotel: '',
  });

  const getFullFileUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http')) return url;

    // Dev ortamında direkt canlı sunucudan çek (Proxy bekleme)
    if (window.location.hostname === 'localhost') {
      return `https://app.akkahotels.com${url}`;
    }

    return `${window.location.origin}${url}`;
  };

  const buildAgencyOptions = (rows) => {
    const optionMap = new Map();

    rows.forEach((item) => {
      const companyName = (item.company_name || '').trim();
      const contactName = `${item.name || ''} ${item.last_name || ''}`.trim();
      const labelBase = companyName || contactName;
      if (!labelBase) return;

      const key = labelBase.toLowerCase();
      if (!optionMap.has(key)) {
        optionMap.set(key, {
          value: labelBase,
          label: companyName && contactName ? `${companyName} (${contactName})` : labelBase,
          contactName,
          phone: item.phone || ''
        });
      }
    });

    return Array.from(optionMap.values());
  };

  const refreshSubAgencyOptions = async (params = { page: 1, page_size: 200 }) => {
    const res = await subAgencyAPI.getSubAgencies(params);
    const payload = res?.data || {};
    const rows =
      payload?.sub_agencies ||
      res?.sub_agencies ||
      payload?.results ||
      res?.results ||
      [];
    const list = Array.isArray(rows) ? rows : [];
    setSubAgencies(list);
    setAgencyOptions(buildAgencyOptions(list));
    return list;
  };

  const buildRepresentativeOptions = (rows) => {
    const optionMap = new Map();

    rows.forEach((item) => {
      const name = (item?.name || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!optionMap.has(key)) {
        optionMap.set(key, {
          value: name,
          label: item?.email ? `${name} (${item.email})` : name,
        });
      }
    });

    return Array.from(optionMap.values());
  };

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset';
      hasFetchedAgencyRef.current = false;
      setAgencySearchTerm('');
      return () => {
        document.body.style.overflow = 'unset';
      };
    }

    document.body.style.overflow = 'hidden';

    const fetchHotels = async () => {
      if (hotels.length > 0) return;
      try {
        const fetchedHotels = await salesApiService.getHotels();
        setHotels(Array.isArray(fetchedHotels) ? fetchedHotels : (fetchedHotels?.data || fetchedHotels?.results || []));
      } catch (error) {
        console.error("Failed to fetch hotels", error);
      }
    };

    const fetchAllSubAgencies = async () => {
      if (hasFetchedAgencyRef.current) return;
      hasFetchedAgencyRef.current = true;

      setIsAgencyLoading(true);
      const loadingGuard = setTimeout(() => {
        setIsAgencyLoading(false);
      }, 10000);

      try {
        await refreshSubAgencyOptions({ page: 1, page_size: 200 });
      } catch (error) {
        console.error("Failed to fetch sub agencies for agency list", error);
      } finally {
        clearTimeout(loadingGuard);
        setIsAgencyLoading(false);
      }
    };

    const fetchAddresses = async () => {
      try {
        const response = await addressAPI.getAddresses({ page_size: 1000 });
        const list = response?.data?.addresses || response?.addresses || [];
        setAddresses(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Failed to fetch addresses", error);
      }
    };

    const fetchRepresentatives = async () => {
      setIsRepresentativeLoading(true);
      try {
        const response = await salesApiService.getRepresentatives();
        const rows =
          response?.data?.results ||
          response?.results ||
          response?.data ||
          response ||
          [];
        setRepresentatives(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Failed to fetch representatives", error);
      } finally {
        setIsRepresentativeLoading(false);
      }
    };

    fetchHotels();
    fetchAllSubAgencies();
    fetchAddresses();
    fetchRepresentatives();

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, hotels.length]);

  useEffect(() => {
    if (!isOpen) return;

    const term = agencySearchTerm.trim();
    if (term.length < 2) return;

    const timer = setTimeout(async () => {
      setIsAgencyLoading(true);
      try {
        await refreshSubAgencyOptions({
          search: term,
          page: 1,
          page_size: 200
        });
      } catch (error) {
        console.error("Failed to search sub agencies", error);
      } finally {
        setIsAgencyLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [agencySearchTerm, isOpen]);

  useEffect(() => {
    const defaultFormData = {
      start_date: '',
      end_date: '',
      agency: '',
      agency_contact: '',
      phone: '',
      type: '',
      country: '',
      city: '',
      address: '',
      communication_type: '',
      call_status: '',
      call_result: '',
      summary: '',
      summary_en: '',
      sales_representative: '',
      pax: '',
      related_call: '',
      hotel: '',
    };

    if (initialData) {
      setFormData({
        ...defaultFormData, // Önce varsayılan boş değerler
        ...initialData, // Üzerine gelen veriler (sadece olanlar güncellenir)
        related_call: initialData.related_call || '',
        hotel: initialData.hotel || '',
      });
    } else {
      setFormData(defaultFormData);
    }
    setSelectedFiles([]);
  }, [initialData, isOpen]);

  const handleFileSelect = (e) => {
    if (e.target.files) {
      setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAgencySelect = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    setFormData(prev => {
      const newState = { ...prev, agency: value };

      const matchingSubAgency = subAgencies.find((sa) => {
        const companyName = (sa.company_name || '').trim();
        const contactName = `${sa.name || ''} ${sa.last_name || ''}`.trim();
        return companyName === value || contactName === value;
      });

      if (selectedOption?.contactName || selectedOption?.phone) {
        if (selectedOption.contactName) {
          newState.agency_contact = selectedOption.contactName;
        }
        if (selectedOption.phone) {
          newState.phone = selectedOption.phone;
        }
      } else if (matchingSubAgency) {
        const fullName = `${matchingSubAgency.name || ''} ${matchingSubAgency.last_name || ''}`.trim();
        if (fullName) {
          newState.agency_contact = fullName;
        }
        if (matchingSubAgency.phone) {
          newState.phone = matchingSubAgency.phone;
        }
      }

      const matchingCall = availableCalls.find(ca => ca.agency === value);
      if (matchingCall) {
        if (matchingCall.agency_contact) {
          newState.agency_contact = matchingCall.agency_contact;
        }
        if (matchingCall.phone) {
          newState.phone = matchingCall.phone;
        }
      }
      return newState;
    });
  };

  const mergedAgencyOptions = useMemo(() => {
    const map = new Map();

    agencyOptions.forEach((opt) => {
      map.set(opt.value.toLowerCase(), opt);
    });

    availableCalls.forEach((call) => {
      const agency = (call.agency || '').trim();
      if (!agency) return;
      const key = agency.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value: agency, label: agency });
      }
    });

    if (formData.agency && !map.has(formData.agency.toLowerCase())) {
      map.set(formData.agency.toLowerCase(), { value: formData.agency, label: formData.agency });
    }

    return Array.from(map.values());
  }, [agencyOptions, availableCalls, formData.agency]);

  const representativeOptions = useMemo(() => {
    const map = new Map();

    buildRepresentativeOptions(representatives).forEach((opt) => {
      map.set(opt.value.toLowerCase(), opt);
    });

    // Mevcut Sales Call kayıtlarından gelen temsilcileri koru
    availableCalls.forEach((call) => {
      const rep = (call.sales_representative || '').trim();
      if (!rep) return;
      const key = rep.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value: rep, label: rep });
      }
    });

    // Düzenlenen kayıtta tablo dışı bir isim varsa yine görünür kalsın
    const currentRep = (formData.sales_representative || '').trim();
    if (currentRep && !map.has(currentRep.toLowerCase())) {
      map.set(currentRep.toLowerCase(), { value: currentRep, label: currentRep });
    }

    return Array.from(map.values());
  }, [representatives, availableCalls, formData.sales_representative]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      pax: formData.pax === '' ? null : formData.pax,
      hotel: formData.hotel ? parseInt(formData.hotel) : null
    };
    onSave(dataToSave, selectedFiles);
  };

  const handleCreateSubAgency = async (subAgencyForm) => {
    try {
      const response = await subAgencyAPI.createSubAgency(subAgencyForm);
      if (response?.success) {
        const created = response?.data || {};
        await refreshSubAgencyOptions({ page: 1, page_size: 200 });

        const agencyValue = (created.company_name || `${created.name || ''} ${created.last_name || ''}`).trim();
        if (agencyValue) {
          setFormData(prev => ({
            ...prev,
            agency: agencyValue,
            agency_contact: `${created.name || ''} ${created.last_name || ''}`.trim() || prev.agency_contact,
            phone: created.phone || prev.phone,
          }));
        }
        setIsSubAgencyModalOpen(false);
      } else {
        alert('Alt acente kaydedilemedi.');
      }
    } catch (error) {
      console.error('Create sub-agency error:', error);
      alert('Alt acente kaydedilemedi.');
    }
  };

  const handleTranslate = async () => {
    if (!formData.summary || !formData.summary.trim()) {
      alert('Lütfen önce ana özet alanını doldurun.');
      return;
    }

    setIsTranslating(true);
    try {
      const response = await contentAPI.translateText(formData.summary, translateLang);
      if (response.success) {
        setFormData(prev => ({
          ...prev,
          summary_en: response.translated_text
        }));
      } else {
        alert('Çeviri sırasında bir hata oluştu: ' + response.message);
      }
    } catch (error) {
      alert('API bağlantı hatası oluştu.');
    } finally {
      setIsTranslating(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto outline-none focus:outline-none">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={onClose}></div>

        <div
          className="relative bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-7xl animate-in fade-in zoom-in duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {initialData?.id ? 'Edit B2B Meetings & Activities' : 'New B2B Meetings & Activities'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-x-4 gap-y-6">
                {/* 1. Row */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Start Date *</Label>
                  <DateTimePicker
                    value={formData.start_date}
                    onChange={(e) => handleChange({ target: { name: 'start_date', value: e.target.value } })}
                    placeholder="Start Date"
                    name="start_date"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">End Date *</Label>
                  <DateTimePicker
                    value={formData.end_date}
                    onChange={(e) => handleChange({ target: { name: 'end_date', value: e.target.value } })}
                    placeholder="End Date"
                    name="end_date"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Agency/To/DMC</Label>
                  <div className="relative">
                    <CreatableSelect
                      className="react-select-container text-xs"
                      classNamePrefix="react-select"
                      options={mergedAgencyOptions}
                      isLoading={isAgencyLoading}
                      loadingMessage={() => "Acenteler yükleniyor..."}
                      noOptionsMessage={() => "Kayıt bulunamadı"}
                      value={mergedAgencyOptions.find(opt => opt.value === formData.agency) || (formData.agency ? { value: formData.agency, label: formData.agency } : null)}
                      onChange={handleAgencySelect}
                      onInputChange={(inputValue, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                          setAgencySearchTerm(inputValue);
                        }
                        return inputValue;
                      }}
                      placeholder="Seçiniz veya yazınız..."
                      isClearable={false}
                      isSearchable
                      formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                      components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '34px',
                          height: '34px',
                          backgroundColor: 'var(--tw-prose-body)',
                          borderColor: '#d1d5db',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 36px 0 8px',
                        }),
                        input: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menuList: (base) => ({
                          ...base,
                          overflowX: 'hidden',
                        }),
                        option: (base) => ({
                          ...base,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }),
                      }}
                      classNames={{
                        control: (state) => `bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white ${state.isFocused ? 'ring-2 ring-blue-500 border-blue-500' : ''}`,
                        menu: () => "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mt-1 rounded-md shadow-lg",
                        menuList: () => "p-1",
                        option: (state) => `cursor-pointer px-3 py-2 rounded-md ${state.isFocused ? 'bg-blue-50 dark:bg-gray-700' : ''} ${state.isSelected ? 'bg-blue-500 text-white' : 'text-gray-900 dark:text-gray-100'}`,
                        singleValue: () => "text-gray-900 dark:text-white",
                        input: () => "text-gray-900 dark:text-white",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setIsSubAgencyModalOpen(true)}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 h-6 w-6 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                      title="Yeni alt acente ekle"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Agency Contact</Label>
                  <Input
                    type="text"
                    name="agency_contact"
                    value={formData.agency_contact}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 text-xs h-[34px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Contact Person"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Phone</Label>
                  <Input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 text-xs h-[34px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Phone"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sales Representative</Label>
                  <CreatableSelect
                    className="react-select-container text-xs"
                    classNamePrefix="react-select"
                    options={representativeOptions}
                    isLoading={isRepresentativeLoading}
                    loadingMessage={() => "Temsilciler yükleniyor..."}
                    noOptionsMessage={() => "Kayıt bulunamadı"}
                    value={
                      representativeOptions.find(opt => opt.value === formData.sales_representative) ||
                      (formData.sales_representative
                        ? { value: formData.sales_representative, label: formData.sales_representative }
                        : null)
                    }
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        sales_representative: selectedOption?.value || ''
                      }));
                    }}
                    placeholder="Seçiniz veya yazınız..."
                    isClearable
                    isSearchable
                    formatCreateLabel={(inputValue) => `"${inputValue}" olarak kullan`}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '34px',
                        height: '34px',
                        borderColor: '#d1d5db',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '0 8px',
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menuList: (base) => ({
                        ...base,
                        overflowX: 'hidden',
                      }),
                      option: (base) => ({
                        ...base,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }),
                    }}
                    classNames={{
                      control: (state) => `bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white ${state.isFocused ? 'ring-2 ring-blue-500 border-blue-500' : ''}`,
                      menu: () => "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mt-1 rounded-md shadow-lg",
                      menuList: () => "p-1",
                      option: (state) => `cursor-pointer px-3 py-2 rounded-md ${state.isFocused ? 'bg-blue-50 dark:bg-gray-700' : ''} ${state.isSelected ? 'bg-blue-500 text-white' : 'text-gray-900 dark:text-gray-100'}`,
                      singleValue: () => "text-gray-900 dark:text-white",
                      input: () => "text-gray-900 dark:text-white",
                    }}
                  />
                </div>

                {/* 2. Row */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Type</Label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose</option>
                    <option value="Sales Call">Sales Call</option>
                    <option value="Workshop">Workshop</option>
                    <option value="Roadshow">Roadshow</option>
                    <option value="Fair">Fair</option>
                    <option value="Webinar">Webinar</option>
                    <option value="AKKA Event">AKKA Event</option>
                    <option value="Info Group (Stay)">Info Group (Stay)</option>
                    <option value="Info Group (Inspection)">Info Group (Inspection)</option>
                    <option value="Whatsapp (Feedback)">Whatsapp (Feedback)</option>
                    <option value="CRM (Feedback by Mail)">CRM (Feedback by Mail)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Country</Label>
                  <Input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 text-xs h-[34px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Country"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Pax</Label>
                  <Input
                    type="number"
                    name="pax"
                    value={formData.pax}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 text-xs h-[34px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white [&::-webkit-inner-spin-button]:appearance-none [appearance:textfield]"
                    placeholder="Pax Count"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">City</Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 text-xs h-[34px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="City"
                  />
                </div>

                {/* 3. Row */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Communication Type</Label>
                  <select
                    name="communication_type"
                    value={formData.communication_type}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose</option>
                    <option value="Yüz Yüze">Face to Face</option>
                    <option value="Telefon">Phone</option>
                    <option value="Video Konferans">Video Conference</option>
                    <option value="E-posta">Email</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Call Status</Label>
                  <select
                    name="call_status"
                    value={formData.call_status}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose</option>
                    <option value="Planlandı">Planned</option>
                    <option value="Tamamlandı">Completed</option>
                    <option value="İptal Edildi">Cancelled</option>
                    <option value="Ertelendi">Postponed</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Call Result</Label>
                  <select
                    name="call_result"
                    value={formData.call_result}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose</option>
                    <option value="Başarılı">Successful</option>
                    <option value="Başarısız">Unsuccessful</option>
                    <option value="Beklemede">Pending</option>
                    <option value="Takip Gerekli">Follow-up Required</option>
                  </select>
                </div>

                {/* Related Call (New) */}
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Related Visit (Optional)</Label>
                  <select
                    name="related_call"
                    value={formData.related_call}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    {availableCalls && availableCalls
                      .filter(c => c.id !== initialData?.id)
                      .slice(0, 50)
                      .map(call => (
                        <option key={call.id} value={call.id}>
                          {call.agency} - {call.sales_representative} ({new Date(call.start_date || call.created_at).toLocaleDateString()})
                        </option>
                      ))
                    }
                  </select>
                </div>

                <div className="md:col-span-1 space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Hotel</Label>
                  <select
                    name="hotel"
                    value={formData.hotel}
                    onChange={handleChange}
                    className="w-full px-2 py-1.5 h-[34px] text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seçiniz (Opsiyonel)</option>
                    {(hotels || []).map(h => (
                      <option key={h.id} value={h.id}>{h.hotel_name}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-5 space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Address</Label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Address details"
                  />
                </div>

                <div className="md:col-span-5 space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Summary (Main)</Label>
                  <textarea
                    name="summary"
                    value={formData.summary}
                    onChange={handleChange}
                    rows={8}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Main summary"
                  />
                </div>

                <div className="md:col-span-5 space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Summary (Report)</Label>
                    <div className="flex items-center gap-2">
                      <select
                        value={translateLang}
                        onChange={(e) => setTranslateLang(e.target.value)}
                        className="text-[10px] py-1 px-1.5 border border-indigo-200 dark:border-indigo-800 rounded bg-indigo-50/50 dark:bg-indigo-900/10 text-indigo-700 dark:text-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="English">English</option>
                        <option value="Russian">Russian</option>
                        <option value="German">German</option>
                        <option value="Turkish">Turkish</option>
                      </select>
                      <button
                        type="button"
                        onClick={handleTranslate}
                        disabled={isTranslating}
                        className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded border border-indigo-200 dark:text-indigo-400 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 dark:border-indigo-800 transition-colors disabled:opacity-50"
                        title="AI ile çevir (Ana Özetten)"
                      >
                        {isTranslating ? (
                          <>
                            <ArrowPathIcon className="w-3 h-3 animate-spin" />
                            Translating...
                          </>
                        ) : (
                          <>
                            <SparklesIcon className="w-3 h-3" />
                            AI Translate
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <textarea
                    name="summary_en"
                    value={formData.summary_en}
                    onChange={handleChange}
                    rows={8}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Report summary (Any language)"
                  />
                </div>

                {/* Attachments Section */}
                <div className="md:col-span-5 space-y-2 border-t pt-4 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Attachments</Label>
                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        id="file-upload"
                        onChange={handleFileSelect}
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50"
                      >
                        <PaperClipIcon className="w-4 h-4 mr-1.5" />
                        Add File
                      </label>
                    </div>
                  </div>

                  {/* Existing Attachments */}
                  {initialData?.attachments?.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Uploaded Files:</div>
                      {initialData.attachments.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md group">
                          <div className="flex items-center overflow-hidden">
                            <PaperClipIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                            <a
                              href={getFullFileUrl(file.file_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 truncate hover:underline"
                            >
                              {file.file_name}
                            </a>
                            <span className="text-[10px] text-gray-400 ml-2 flex-shrink-0">
                              ({new Date(file.uploaded_at).toLocaleDateString()})
                            </span>
                          </div>
                          {onDeleteAttachment && (
                            <button
                              type="button"
                              onClick={() => onDeleteAttachment(file.id)}
                              className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete file"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* New Selected Files */}
                  {selectedFiles.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">New Files (will be uploaded):</div>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                          <div className="flex items-center overflow-hidden">
                            <PaperClipIcon className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                            <span className="text-xs text-gray-700 dark:text-gray-200 truncate">{file.name}</span>
                            <span className="text-[10px] text-gray-400 ml-2 flex-shrink-0">
                              {(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            className="text-gray-400 hover:text-red-500"
                            title="Remove"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end space-x-3">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  className="px-4 py-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {initialData ? 'Update' : 'Save'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        <SubAgencyFormModal
          isOpen={isSubAgencyModalOpen}
          onClose={() => setIsSubAgencyModalOpen(false)}
          onSave={handleCreateSubAgency}
          editingItem={null}
          addresses={addresses}
        />
      </div>
    </div>,
    document.body
  );
};

export default SalesCallModal;
